import { ExclamationCircleOutlined, MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox, Col, Row } from "antd";
import { useAvailableCodesQuery, useSelectedCodesQuery } from "api/queries/groups";
import KEYS from "api/queries/keys";
import { useTeamMembersQuery } from "api/queries/teamMembers";
import { AxiosResponse } from "axios";
import { Button, Divider, Drawer, Form, Input, Modal, Select, Spin, Table } from "components/styleguide";
import ForumGroupType from "enums/ForumGroupType";
import {
  AddDeleteParticipationCodeModel,
  ForumGroupRequestModel,
  ForumGroupResponseModel,
  PageResponseParticipationCodeModel,
  ParticipationCodeModel,
} from "generated/api";
import { useModernQueryWithPaginationAndOrder } from "hooks";
import { toNumber } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getColumnSearchProps } from "utils/tableColumnUtils";
import { validateMaxStringLength } from "utils/validatorUtils";

import useUnsavedChangesPrompt from "../../Timeslots/useUnsavedChangesPrompt";

import styles from "./styles.module.less";

const { Option } = Select;

type EditGroupRequest = ForumGroupRequestModel & {
  groupId: number;
  participationCodesToAdd?: AddDeleteParticipationCodeModel[];
  participationCodesToRemove?: AddDeleteParticipationCodeModel[];
};

type GroupFormValues = Pick<ForumGroupRequestModel, "name" | "description" | "groupType" | "primary" | "contactUserId">;

const getCode = (code: ParticipationCodeModel | AddDeleteParticipationCodeModel) =>
  ("code" in code ? code.code : code.participationCode) ?? "";

const toCodeRequest = (code: ParticipationCodeModel): AddDeleteParticipationCodeModel => ({
  participationCode: code.code,
});

const hasCode = (codes: ParticipationCodeModel[], code: ParticipationCodeModel) =>
  codes.some((item) => getCode(item) === getCode(code));

const addUniqueCode = (codes: ParticipationCodeModel[] | undefined, code: ParticipationCodeModel) => [
  code,
  ...(codes ?? []).filter((item) => getCode(item) !== getCode(code)),
];

const removeCodeFromList = (codes: ParticipationCodeModel[] | undefined, code: ParticipationCodeModel) =>
  (codes ?? []).filter((item) => getCode(item) !== getCode(code));

const getInitialValues = (group: ForumGroupResponseModel | null | undefined): GroupFormValues => ({
  name: group?.name,
  description: group?.description,
  groupType: group?.groupType,
  primary: group?.primary ?? false,
  contactUserId: group?.contactUserId ?? undefined,
});

const normalizeValues = (values: GroupFormValues) => ({
  name: values.name ?? "",
  description: values.description ?? "",
  groupType: values.groupType ?? null,
  primary: values.primary ?? false,
  contactUserId: values.contactUserId ?? null,
});

export default function EditGroupDrawer({
  visible,
  isLoading,
  onClose,
  onFinish,
  group,
}: {
  visible: boolean;
  group: ForumGroupResponseModel | null | undefined;
  isLoading: boolean;
  onClose: () => void;
  onFinish: (group: EditGroupRequest) => Promise<AxiosResponse<ForumGroupResponseModel>>;
}) {
  const { id: forumId } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [availableCodes, setAvailableCodes] = useState<ParticipationCodeModel[] | undefined>([]);
  const [selectedCodes, setSelectedCodes] = useState<ParticipationCodeModel[] | undefined>([]);
  const [pendingCodesToAdd, setPendingCodesToAdd] = useState<ParticipationCodeModel[]>([]);
  const [pendingCodesToRemove, setPendingCodesToRemove] = useState<ParticipationCodeModel[]>([]);
  const [formValues, setFormValues] = useState<GroupFormValues>(getInitialValues(group));
  const [availableCodeSearch, setAvailableCodeSearch] = useState<string | undefined>();
  const [availableDescrSearch, setAvailableDesrcSearch] = useState<string | undefined>();
  const [selectedCodeSearch, setSelectedCodeSearch] = useState<string | undefined>();
  const [selectedDescrSearch, setSelectedDescrSearch] = useState<string | undefined>();

  const pendingCodesToAddRef = useRef<ParticipationCodeModel[]>([]);
  const pendingCodesToRemoveRef = useRef<ParticipationCodeModel[]>([]);

  useEffect(() => {
    pendingCodesToAddRef.current = pendingCodesToAdd;
  }, [pendingCodesToAdd]);

  useEffect(() => {
    pendingCodesToRemoveRef.current = pendingCodesToRemove;
  }, [pendingCodesToRemove]);

  const resetPendingCodeChanges = () => {
    setPendingCodesToAdd([]);
    setPendingCodesToRemove([]);
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    const initialValues = getInitialValues(group);
    form.setFieldsValue(initialValues);
    setFormValues(initialValues);
    resetPendingCodeChanges();
  }, [form, group, visible]);

  const isFormChanged = useMemo(() => {
    return JSON.stringify(normalizeValues(formValues)) !== JSON.stringify(normalizeValues(getInitialValues(group)));
  }, [formValues, group]);

  const hasUnsavedChanges = isFormChanged || pendingCodesToAdd.length > 0 || pendingCodesToRemove.length > 0;
  const unsavedChangesMessage = t("Unsaved changes will be lost. Do you want to leave without saving?");
  const pendingCodesToAddValues = useMemo(() => pendingCodesToAdd.map(getCode), [pendingCodesToAdd]);
  const pendingCodesToRemoveValues = useMemo(() => pendingCodesToRemove.map(getCode), [pendingCodesToRemove]);
  const pendingCodesToAddSummary = useMemo(
    () => pendingCodesToAddValues.map((code) => `[${code}]`).join(", "),
    [pendingCodesToAddValues],
  );
  const pendingCodesToRemoveSummary = useMemo(
    () => pendingCodesToRemoveValues.map((code) => `[${code}]`).join(", "),
    [pendingCodesToRemoveValues],
  );

  useUnsavedChangesPrompt(visible && hasUnsavedChanges, isLoading, unsavedChangesMessage);

  const { response: availableCodesQuery, pagination: availablePagination } = useModernQueryWithPaginationAndOrder(
    useAvailableCodesQuery,
    {
      onSuccess: ({ data }: { data: PageResponseParticipationCodeModel }) => {
        setAvailableCodes(data.items);
      },
      enabled: visible && !!forumId && !!group?.id,
    },
    {
      forumId,
      groupId: group?.id,
      code: availableCodeSearch ?? undefined,
      description: availableDescrSearch ?? undefined,
      participationCodesToAdd: pendingCodesToAddValues,
      participationCodesToRemove: pendingCodesToRemoveValues,
    },
    { defaultPageSize: 10, pageSizeOptions: [10, 20] },
  );

  const { response: selectedCodesQuery, pagination: selectedPagination } = useModernQueryWithPaginationAndOrder(
    useSelectedCodesQuery,
    {
      onSuccess: ({ data }: { data: PageResponseParticipationCodeModel }) => {
        setSelectedCodes(data.items);
      },
      enabled: visible && !!forumId && !!group?.id,
    },
    {
      forumId,
      groupId: group?.id,
      code: selectedCodeSearch ?? undefined,
      description: selectedDescrSearch ?? undefined,
      participationCodesToAdd: pendingCodesToAddValues,
      participationCodesToRemove: pendingCodesToRemoveValues,
    },
    { defaultPageSize: 10, pageSizeOptions: [10, 20] },
  );

  const addCode = (code: ParticipationCodeModel) => {
    const wasPendingRemoval = hasCode(pendingCodesToRemoveRef.current, code);

    setPendingCodesToRemove((codes) => removeCodeFromList(codes, code));
    if (!wasPendingRemoval) {
      setPendingCodesToAdd((codes) => addUniqueCode(codes, code));
    }

    setAvailableCodes((codes) => removeCodeFromList(codes, code));
    setSelectedCodes((codes) => addUniqueCode(codes, code));
  };

  const removeCode = (code: ParticipationCodeModel) => {
    const wasPendingAddition = hasCode(pendingCodesToAddRef.current, code);

    setPendingCodesToAdd((codes) => removeCodeFromList(codes, code));
    if (!wasPendingAddition) {
      setPendingCodesToRemove((codes) => addUniqueCode(codes, code));
    }

    setSelectedCodes((codes) => removeCodeFromList(codes, code));
    setAvailableCodes((codes) => addUniqueCode(codes, code));
  };

  const { data: { data: forumTeamMembers } = {}, isLoading: isTeamMembersLoading } = useTeamMembersQuery(
    { forumId: toNumber(forumId) },
    { enabled: !!forumId },
  );

  const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string, searchSet: string) => {
    if (searchSet === "available") {
      if (dataIndex === "code") {
        setAvailableCodeSearch(selectedKeys[0]);
      } else {
        setAvailableDesrcSearch(selectedKeys[0]);
      }
      queryClient.invalidateQueries([KEYS.GET_AVAILABLE_CODES]);
    } else {
      if (dataIndex === "code") {
        setSelectedCodeSearch(selectedKeys[0]);
      } else {
        setSelectedDescrSearch(selectedKeys[0]);
      }
    }
    confirm();
  };

  const handleReset = (clearFilters: () => void, dataIndex: string, searchSet: string, confirm: () => void) => {
    if (searchSet === "available") {
      if (dataIndex === "code") {
        setAvailableCodeSearch(undefined);
      } else {
        setAvailableDesrcSearch(undefined);
      }
    } else {
      if (dataIndex === "code") {
        setSelectedCodeSearch(undefined);
      } else {
        setSelectedDescrSearch(undefined);
      }
    }
    clearFilters();
    confirm();
  };

  const resetAllSearch = () => {
    setAvailableCodeSearch(undefined);
    setAvailableDesrcSearch(undefined);
    setSelectedCodeSearch(undefined);
    setSelectedDescrSearch(undefined);
  };

  const closeWithoutSaving = () => {
    resetAllSearch();
    resetPendingCodeChanges();
    onClose();
  };

  const requestClose = () => {
    if (!hasUnsavedChanges) {
      closeWithoutSaving();
      return;
    }

    Modal.confirm({
      title: t("confirmation"),
      content: unsavedChangesMessage,
      icon: <ExclamationCircleOutlined />,
      okText: t("yes"),
      cancelText: t("no"),
      onOk: closeWithoutSaving,
    });
  };

  const availableCodeColumns = [
    {
      title: t("peopleType"),
      dataIndex: "code",
      ...getColumnSearchProps("code", handleSearch, handleReset, "available", null, styles.filteredSearch),
    },
    {
      title: t("Description"),
      dataIndex: "description",
      ...getColumnSearchProps("description", handleSearch, handleReset, "available", null, styles.filteredSearch),
    },
    {
      title: "",
      render: (_: unknown, record: ParticipationCodeModel) => {
        return (
          <Button
            className={styles.codeIcon}
            type="link"
            icon={<PlusCircleOutlined />}
            aria-label={t("Include participation code")}
            title={t("Include participation code")}
            onClick={() => addCode(record)}
          />
        );
      },
    },
  ];

  const selectedCodeColumns = [
    {
      title: t("peopleType"),
      dataIndex: "code",
      ...getColumnSearchProps("code", handleSearch, handleReset, "selected", null, styles.filteredSearch),
    },
    {
      title: t("Description"),
      dataIndex: "description",
      ...getColumnSearchProps("description", handleSearch, handleReset, "selected", null, styles.filteredSearch),
    },
    {
      title: "",
      render: (_: unknown, record: ParticipationCodeModel) => {
        return (
          <Button
            className={styles.codeIcon}
            type="link"
            icon={<MinusCircleOutlined />}
            aria-label={t("Remove code")}
            title={t("Remove code")}
            onClick={() => removeCode(record)}
          />
        );
      },
    },
  ];

  return (
    <Drawer
      className="group-drawer"
      title={"Participation Codes"}
      placement="right"
      onClose={requestClose}
      open={visible}
      contentWrapperStyle={{ minWidth: "55%" }}
      destroyOnClose
    >
      <Form
        form={form}
        id="groupForm"
        name="group-form"
        className={styles.groupForm}
        labelCol={{ span: 4 }}
        onValuesChange={() => setFormValues(form.getFieldsValue())}
        onFinish={async (values) => {
          try {
            await onFinish({
              ...values,
              groupId: toNumber(group?.id),
              participationCodesToAdd: pendingCodesToAdd.map(toCodeRequest),
              participationCodesToRemove: pendingCodesToRemove.map(toCodeRequest),
            });
            resetAllSearch();
            resetPendingCodeChanges();
            await queryClient.resetQueries([KEYS.GET_AVAILABLE_CODES]);
            await queryClient.resetQueries([KEYS.GET_SELECTED_CODES]);
          } catch {
            return;
          }
        }}
      >
        <div className={styles.formLayout}>
          <div>
            <Form.Item
              label="Name"
              name="name"
              initialValue={group?.name}
              rules={[{ required: true, message: "Group name is required!" }]}
            >
              <Input disabled={group?.isRestricted} />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              initialValue={group?.description}
              rules={[
                { required: true, message: "Group description is required!" },
                {
                  validator: validateMaxStringLength(200),
                  message: t("'Description' field has a maximum of 200 characters"),
                },
              ]}
            >
              <Input disabled={group?.isRestricted} />
            </Form.Item>
            <Form.Item
              label={t("Contact")}
              name="contactUserId"
              initialValue={group?.contactUserId}
              rules={[
                {
                  required: true,
                  message: t("This field is mandatory!"),
                },
              ]}
            >
              <Select allowClear>
                {isTeamMembersLoading ? (
                  <div>
                    <Spin />
                  </div>
                ) : (
                  forumTeamMembers?.map(({ name, userId }, index) => (
                    <Option value={userId} key={index}>
                      {name}
                    </Option>
                  ))
                )}
              </Select>
            </Form.Item>
            <Form.Item
              label="Group Type"
              name="groupType"
              initialValue={group?.groupType}
              rules={[{ required: true, message: "Group type is required!" }]}
            >
              <Select>
                <Option value={1} key={ForumGroupType.DisplayType}>
                  {t(`forumGroupTypes.${ForumGroupType.toDisplayName(ForumGroupType.DisplayType)}`)}
                </Option>
                <Option value={2} key={ForumGroupType.General}>
                  {t(`forumGroupTypes.${ForumGroupType.toDisplayName(ForumGroupType.General)}`)}
                </Option>
                <Option value={3} key={ForumGroupType.Selection}>
                  {t(`forumGroupTypes.${ForumGroupType.toDisplayName(ForumGroupType.Selection)}`)}
                </Option>
              </Select>
            </Form.Item>
            <Form.Item label="Primary" name="primary" valuePropName="checked" initialValue={group?.primary}>
              <Checkbox />
            </Form.Item>
          </div>
          <div>
            <Row gutter={16} className={styles.codeTableHeaders}>
              <Col span={12}>
                <h3 className={styles.codeTableTitle}>
                  <strong>{t("Available Codes")}</strong>
                </h3>
                <div className={styles.pendingCodesSummary}>
                  <strong>{t("Pending Remove")}:</strong> {pendingCodesToRemoveSummary}
                </div>
              </Col>
              <Col span={12}>
                <h3 className={styles.codeTableTitle}>
                  <strong>{t("Selected Codes")}</strong>
                </h3>
                <div className={styles.pendingCodesSummary}>
                  <strong>{t("Pending Include")}:</strong> {pendingCodesToAddSummary}
                </div>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Table
                  bordered={true}
                  columns={availableCodeColumns}
                  dataSource={availableCodes}
                  loading={availableCodesQuery.isLoading || selectedCodesQuery.isLoading}
                  pagination={availablePagination}
                  rowClassName={(record: ParticipationCodeModel) =>
                    hasCode(pendingCodesToRemove, record) ? styles.pendingCodeRemove : ""
                  }
                  rowKey="code"
                ></Table>
              </Col>
              <Col span={12}>
                <Table
                  columns={selectedCodeColumns}
                  dataSource={selectedCodes}
                  bordered={true}
                  loading={availableCodesQuery.isLoading || selectedCodesQuery.isLoading}
                  pagination={selectedPagination}
                  rowClassName={(record: ParticipationCodeModel) =>
                    hasCode(pendingCodesToAdd, record) ? styles.pendingCodeAdd : ""
                  }
                  rowKey="code"
                ></Table>
              </Col>
            </Row>
          </div>
          <div>
            <Divider />
            <Row>
              <Col span={3}>
                <Button onClick={requestClose}>{t("Cancel")}</Button>
              </Col>
              <Col span={18} />
              <Col span={3}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={isLoading} disabled={!hasUnsavedChanges}>
                    {t("Update")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>
      </Form>
    </Drawer>
  );
}

EditGroupDrawer.propTypes = {
  isLoading: PropTypes.bool,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onFinish: PropTypes.func,
  group: PropTypes.object,
};
